import classNames from "classnames";
import {Button} from "../../atoms/button";
import {createRef, useContext, useState} from "react";
import {Dictionary} from "../../../types/hygiena-types";
import {DictionaryContext} from "../../../context/dictionary-context";

type RLUCalculatorItem = {
  title: string,
  plus: number,
  divisor: number,
};

type RLUResult = {
  title: string,
  upper: number,
  lower: number,
};

const TITLE = 0;
const PLUS = 1;
const DIVISOR = 2;

export function ParagraphRLUCalculator({paragraph, ...props}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const table = paragraph?.field_rlu_calculator_items;
  const items: RLUCalculatorItem[] = []
  let rows = Object.keys(table?.value ?? {}).map(item => table?.value?.[item]);
  rows = rows?.length ? rows.sort((a, b) => a.weight - b.weight) : [];

  const instrumentRef = createRef<HTMLSelectElement>()
  const [upperLimit, setUpperLimit] = useState<number>(0);
  const [lowerLimit, setLowerLimit] = useState<number>(0);
  const [results, setResults] = useState<RLUResult[]>();

  const limitsSet = !isNaN(lowerLimit ?? NaN) && !isNaN(upperLimit ?? NaN);

  if (rows?.length) rows.shift();
  if (!rows?.length) return <></>

  for (const row of rows) {
    if (!row?.[TITLE] || !row?.[PLUS] || !row?.[DIVISOR]) continue;
    const title = row[TITLE];
    const plus = parseFloat(row[PLUS]);
    const divisor = parseFloat(row[DIVISOR]);
    if (title?.length && !isNaN(plus) && !isNaN(divisor)) {
      items.push({title, plus, divisor,})
    }
  }

  function calculate() {
    if (!instrumentRef?.current?.value?.length) return;
    const calculatorItem = items.filter(item => item?.title === instrumentRef?.current?.value)?.[0];
    if (calculatorItem) {
      let lowerVal = Math.floor(lowerLimit);
      let upperVal = Math.floor(upperLimit);
      if (calculatorItem?.divisor > 0) {
        lowerVal =  Math.floor((lowerLimit + calculatorItem.plus)/calculatorItem.divisor);
        upperVal =  Math.floor((upperVal + calculatorItem.plus)/calculatorItem.divisor);
      }
      setResults([
        { title: "EnSURE Touch / EnSURE", lower: lowerVal, upper: upperVal },
        { title: "SystemSURE Plus", lower: Math.floor(lowerVal / 2), upper: Math.floor(upperVal * .5)},
      ]);
    }
    else setResults([]);
  }

  return (
    <>
      {items?.length ? (
        <>
          {/* Options */}
          <div className="bg-white flex flex-col md:flex-row p-6 gap-2 md:gap-6 text-black items-center justify-end mb-4">
            {/* Instrument */}
            <label className="font-semibold max-md:w-full" htmlFor="instrument">
              {`${t?.calculator?.select_instrument}`}
              <select name={"instrument"}
                      ref={instrumentRef}
                      className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full">
                {items.map((item, index) => (
                  <option key={`instrument--${index}`} className="bg-white">{item.title}</option>
                ))}
              </select>
            </label>
            {/* Lower RLU Limit */}
            <label className="font-semibold max-md:w-full" htmlFor="lower">
              {`${t?.calculator?.lower_rlu_limit}`}
              <input name="lower" type="number"
                     onChange={(event) => setLowerLimit(parseFloat(event?.target?.value))}
                     defaultValue={lowerLimit}
                     className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full"/>
            </label>
            {/* Upper RLU Limit */}
            <label className="font-semibold max-md:w-full" htmlFor="upper">
              {`${t?.calculator?.upper_rlu_limit}`}
              <input name="upper" type="number"
                     onChange={(event) => setUpperLimit(parseFloat(event?.target?.value))}
                     defaultValue={upperLimit}
                     className="block px-4 py-2 border-hygienaGray border rounded-sm font-normal mt-2 w-full"/>
            </label>
            <Button callback={calculate} containerClassName="self-end max-md:w-full"
                    className={classNames(
                      "max-md:w-full",
                      {"pointer-events-none opacity-50": !limitsSet}
                    )}
            >
              {`${t?.calculator?.calculate}`}
            </Button>
          </div>
          {results?.length ? (
            <div className="animate-fade-in">
              <div className="md:text-xl">
                {`${t?.calculator?.hygiena_recommends}`}
              </div>
              <div className="overflow-auto">
                <table className="w-full table-auto max-h-[80vh] md:max-h-none mt-4">
                  <thead className="sticky top-0 text-left text-black bg-white">
                    <tr className="border-b-primary border-b-2">
                      {['Instrument', 'Lower RLU', 'Upper RLU'].map((label, index) => (
                        <th key={`${paragraph?.id}--${label}--${index}`} className={classNames(
                          "py-3 px-2 lg:text-xl font-semibold",
                          {
                            "pl-6": index === 0,
                            "text-center": index !== 0,
                          })}
                        >{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, index) => (
                      <tr key={`${paragraph?.id}--result--${index}`} className="even:bg-hygienaLight odd:bg-white border-b border-b-gray-200">
                        <td className="p-4 pl-6 font-semibold">{row.title}</td>
                        <td className="p-4 text-center">{row.lower}</td>
                        <td className="p-4 text-center">{row.upper}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ): <></>}
        </>
      ): <></>}
    </>
  )
}